
import {map} from 'rxjs/operators';

import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable, Subject } from "rxjs";

import { Contact, ContactStub, AgentType, LegacyContact, LkpLegacyRepresentativeType } from "../models/contact.model";
import { genId } from "../../core/services/genId";
import { ContactGroup } from "../models/contactGroup.model";
import { ContactHistoryEvent, ContactEventType } from '../models/contactHistory.model';


export interface ContactSearchSpec {
    ids?: string[];
    internal?: boolean;
    contains?: string;
    stubs?: boolean;
    organizationId?: string;
    isUser?: boolean;
    canLogIn?: boolean;
}

export interface ContactHistorySearchSpec {
    startDate?: Date;
    endDate?: Date;
    eventTypes?: ContactEventType[];
    contactIds?: string[];

}

@Injectable()
export class ContactService {
    getContactsByOrganization(id: string): Observable<Contact[]> {
        return this.http.get<Contact[]>(`/api/admin/contacts/by-organization/${id}`);
    }

    constructor(public readonly http: HttpClient) {
    }

    getLegacyContacts(organizationId: string, historicalOwnerNumber: string): Observable<LegacyContact[]> {
        let params = new HttpParams();
        if (historicalOwnerNumber) {
            params = params.set('historicalOwnerNumber', historicalOwnerNumber);
        }
        return this.http.get<LegacyContact[]>(`/api/admin/contacts/legacy/${organizationId}`, { params });
    }

    updateLegacyAgentType(contact: LegacyContact): Observable<LegacyContact> {
        return this.http.put<LegacyContact>(`/api/admin/contacts/update-agent-type/`, contact);
    }

    getLegacyRepresentativeTypes(): Observable<LkpLegacyRepresentativeType[]> {
        return this.http.get<LkpLegacyRepresentativeType[]>(`/api/admin/contacts/legacy/type-lookup`);
    }

    getContactGroup(contactId: string, includeUserCount: boolean = false): Observable<ContactGroup> {
        return this.http.get<ContactGroup>(`/api/admin/contactgroup/${contactId}/${includeUserCount}`);
    }

    getAll<T extends Contact[]>(spec: ContactSearchSpec): Observable<T> {
        return this.http.post<T>("/api/admin/contacts", spec);
    }

    save(contact: Contact): Observable<Contact> {
        if (contact.id === null || contact.id.match(/^\s+$/)) {
            contact.id = genId();
        }
        return this.http.post<Contact>(`/api/admin/contacts/${contact.id}`, contact);
    }

    delete(id: string, orgId: string): Observable<Contact> {
        return this.http.delete<Contact>(`/api/admin/contacts/${id}/${orgId}`);
    }

    getAgentForOrganization(type: AgentType, orgId: string): Observable<Contact> {
        return this.http.get<Contact[]>(`/api/admin/contacts/by-organization/${orgId}`).pipe(
            map(cs => {
                let candidates = cs.filter(c => (c.agentType & type) !== 0);

                if (candidates.length >= 1) return candidates[0];
                else return null;
            }))
    }

    setCanLogIn(id: string, canLogIn: boolean): Observable<Contact> {
        return this.http.put<Contact>(`/api/admin/contacts/canlogin/${id}/${canLogIn}`, null);
    }

    updateContactGroups(contactId: string, groupIds: string[]): Observable<Contact> {
        let queryString = '?';
        groupIds.forEach(id => queryString += 'groupIds=' + id + '&');

        return this.http.put<Contact>(`/api/admin/contact/${contactId}/groups${queryString}`, null);
    }

    // gets the contact profile of the signed-in user
    getProfile(): Observable<Contact> {
        return this.http.get<Contact>("api/admin/contact/profile");
    }

    getInternalContactsByAuthority(authority: string): Observable<Contact[]> {
        return this.http.get<Contact[]>(`/api/admin/contact/getByAuthority/${authority}`);
    }

    getContactHistory(contactId: string): Observable<ContactHistoryEvent[]> {
        return this.http.get<ContactHistoryEvent[]>(`/api/admin/contactHistory/${contactId}`);
    }
}